<template>
  <div
    v-if="isRequestPasscodeForm"
    class="cf-flex cf-flex--column cf-flex--gap-16 cf-padding-bottom-16"
  >
    <h2 class="cf-title cf-title--2">
      {{ requestPasscodeContent.title }}
    </h2>

    <p class="cf-subtitle cf-subtitle--2">
      {{ requestPasscodeContent.description }}
    </p>

    <cf-validated-form
      name="userContactInfoPasscodeForm"
      @valid-submit="getPasscodeHandler"
    >
      <div class="cf-flex cf-flex--column cf-flex--gap-12">
        <cf-phone-number-input
          v-if="isPhoneNumberSignIn"
          key="phone"
          v-model="phoneNumber"
          label="Phone Number"
          name="phoneNumberSignIn"
          :validations="{ required: true }"
        />

        <cf-text-input
          v-else
          key="email"
          v-model="email"
          type="email"
          label="Email"
          name="emailSignIn"
          :validations="{ required: true }"
        />

        <button
          :class="['cf-button cf-button--primary cf-button--lg cf-button--fill', { 'cf-button--loading': isRequestPasscodeLoading }]"
          type="submit"
        >
          {{ requestPasscodeContent.cta }}
        </button>
      </div>
    </cf-validated-form>
  </div>

  <div
    v-else
    class="cf-flex cf-flex--column cf-flex--gap-16 cf-padding-bottom-16 animation-slide-left"
  >
    <h2 class="cf-title cf-title--2">
      {{ sendPasscodeContent.title }}
    </h2>

    <p
      class="cf-subtitle cf-subtitle--2"
      v-html="sendPasscodeContent.description"
    />

    <cf-validated-form
      name="submitPasscodeForm"
      @valid-submit="submitPasscodeHandler"
    >
      <div class="cf-flex cf-flex--column cf-flex--gap-12">
        <cf-text-input
          v-model="verificationCode"
          type="text"
          label="Passcode"
          icon-class="fa-key"
          name="passcode"
          inputmode="numeric"
          pattern="[0-9]*"
          autocomplete="one-time-code"
          class="cf-text-input--spaced-characters"
          :validations="{ required: true }"
        />

        <p class="cf-subtitle cf-subtitle--2">
          Didn't receive the passcode?

          <span
            class="cf-link cf-link--brand-secondary cf-link--heavy"
            @click="resendPasscodeHandler"
          >
            Resend It
          </span>
        </p>

        <button
          :class="['cf-button cf-button--primary cf-button--lg cf-button--fill', { 'cf-button--loading': isSubmittingPasscode }]"
          type="submit"
        >
          {{ sendPasscodeContent.cta }}
        </button>
      </div>
    </cf-validated-form>

    <button
      class="cf-button cf-button--tertiary cf-button--lg cf-button--fill"
      type="submit"
      @click="backToRequestPasswordForm"
    >
      Back
    </button>
  </div>
</template>


<script>
  import { mapGetters } from 'vuex';

  import CfValidatedForm from '@/view/components/shared/cf-validated-form/index.vue';
  import CfPhoneNumberInput from '@/view/components/shared/cf-phone-number-input/index.vue';
  import CfTextInput from '@/view/components/shared/cf-text-input/index.vue';


  export default {
    name: 'SignInWithPasscode',

    components: {
      CfValidatedForm,
      CfPhoneNumberInput,
      CfTextInput
    },

    props: {
      signInMethod: {
        type: String,
        required: true
      }
    },

    data: () => ({
      isRequestPasscodeForm: true,
      isRequestPasscodeLoading: false,
      phoneNumber: '',
      email: '',
      verificationCode: '',
      isSubmittingPasscode: false
    }),

    computed: {
      ...mapGetters('content', ['content']),

      isPhoneNumberSignIn() {
        return this.signInMethod === 'phoneNumber';
      },

      passcodeContent() {
        return this.content.SignInWithCode.metadata;
      },

      requestPasscodeContent() {
        switch (this.signInMethod) {
          case 'email':
            return {
              title: this.passcodeContent.enterEmailTitle,
              description: this.passcodeContent.enterEmailDescription,
              cta: this.passcodeContent.enterEmailCta
            };
          case 'phoneNumber':
            return {
              title: this.passcodeContent.enterPhoneNumberTitle,
              description: this.passcodeContent.enterPhoneNumberDescription,
              cta: this.passcodeContent.enterEmailCta
            };
          default:
            return {
              title: 'Welcome Back',
              description: 'Enter your contact information',
              cta: 'Request Passcode'
            };
        }
      },

      contactInfo() {
        return this.isPhoneNumberSignIn ? this.phoneNumber : this.email;
      },

      sendPasscodeContent() {
        switch (this.signInMethod) {
          case 'email':
            return {
              title: this.passcodeContent.verifyEmailTitle,
              description: this.findAndReplaceContent(this.passcodeContent.verifyEmailDescription, this.email),
              cta: this.passcodeContent.verifyEmailCta
            };
          case 'phoneNumber':
            return {
              title: this.passcodeContent.verifyPhoneNumberTitle,
              description: this.findAndReplaceContent(this.passcodeContent.verifyPhoneNumberDescription, this.phoneNumber),
              cta: this.passcodeContent.verifyPhoneNumberCta
            };
          default:
            return {
              title: 'Verify',
              description: 'Enter passcode sent to you',
              cta: 'Send'
            };
        }
      }
    },

    methods: {
      async getPasscodeHandler() {
        this.isRequestPasscodeForm = false;

        this.isRequestPasscodeLoading = true;

        await this.resendPasscodeHandler();

        this.isRequestPasscodeLoading = false;
      },

      async resendPasscodeHandler() {
        const dispatchPayload = { contactInfo: this.contactInfo, contactType: this.signInMethod };

        await this.$store.dispatch('session/resendTwoFactorAuthPasscode', dispatchPayload);
      },

      findAndReplaceContent(content, replacementString) {
        // takes content and replaces double curly's {{}} with replacementString
        const result = content.replace(/{{(.*?)}}/g, `<b>${replacementString}<b>`);

        return result;
      },

      async submitPasscodeHandler() {
        this.isSubmittingPasscode = true;

        const dispatchPayload = { contactInfo: this.contactInfo, contactType: this.signInMethod, verificationCode: this.verificationCode };

        const { isSuccess } = await this.$store.dispatch('session/submitTwoFactorAuthPasscode', dispatchPayload);

        if (isSuccess) {
          this.$closeAllModals();
        }

        this.isSubmittingPasscode = false;
      },

      backToRequestPasswordForm() {
        this.isRequestPasscodeForm = true;
      }
    }
  };
</script>
